import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Email } from '../types';
import api, { apiBaseUrl } from '../services/api';
import websocketService from '../services/websocket';
import { getMailboxAccessToken } from '../services/mailboxAccess';
import { formatDateTimeMsk } from '../utils/dateTime';
import { extractVerificationCode } from '../utils/verificationCode';
import { sanitizeEmailHtml } from '../utils/htmlSanitizer';
import createRipple from '../utils/ripple';
import './MailboxView.css';

const CODE_SCAN_EMAILS_LIMIT = 60;
const MAX_EMAILS_IN_MEMORY = 1200;
const MAX_INLINE_DATA_URI_LEN = 4000;
const COLLAPSED_EMAIL_ESTIMATED_HEIGHT = 108;
const VIRTUAL_OVERSCAN_PX = 360;
const VIRTUALIZATION_MIN_COUNT = 20;

interface MailboxViewProps {
  mailboxId: string;
  onBack: () => void;
  isWideLayout?: boolean;
}

type EmailContentEntry = {
  mode: 'text' | 'html' | 'safe';
  content: string;
  blobUrl?: string;
  initialHeight?: number;
};

/* Аватар отправителя: буква + стабильный оттенок из адреса */
const getSenderVisual = (fromEmail: string) => {
  const source = String(fromEmail || '?');
  let hash = 0;
  for (let i = 0; i < source.length; i += 1) {
    hash = (hash * 31 + source.charCodeAt(i)) | 0;
  }
  const hue = Math.abs(hash) % 360;
  const letterMatch = source.match(/[a-zA-Zа-яА-ЯёЁ0-9]/);
  return { hue, letter: (letterMatch ? letterMatch[0] : '@').toUpperCase() };
};

/* Домен сервиса из адреса вида `Name <user@domain.com>` или `user@domain.com` */
const senderDomain = (fromEmail: string): string | null => {
  const m = String(fromEmail || '').toLowerCase().match(/@([a-z0-9][a-z0-9.-]*\.[a-z]{2,})/);
  return m ? m[1] : null;
};

/* Домены, у которых фавиконка не нашлась (404) — не ретраим в рамках сессии */
const failedAvatarDomains = new Set<string>();
/* Домены, чьи иконки уже загружались в этой сессии: такие показываем
   мгновенно, без анимации проявления */
const loadedAvatarDomains = new Set<string>();
/* Кэш анализа иконки по домену: цвет подложки + «круглая ли» иконка */
type AvatarIconMeta = { bg: string | null; round: boolean };
const avatarMetaByDomain = new Map<string, AvatarIconMeta>();
const AVATAR_META_FALLBACK: AvatarIconMeta = { bg: null, round: false };

/* Анализ фавиконки: доминирующий цвет края (им заливаем пустоту круга) и
   определение круглых иконок — их растягиваем на весь аватар, чтобы не было
   «круга в круге» с видимым смещением (Facebook и т.п.) */
const analyzeIcon = (img: HTMLImageElement): AvatarIconMeta => {
  try {
    const S = 32;
    const canvas = document.createElement('canvas');
    canvas.width = S;
    canvas.height = S;
    const ctx = canvas.getContext('2d');
    if (!ctx) return AVATAR_META_FALLBACK;
    ctx.drawImage(img, 0, 0, S, S);
    const { data } = ctx.getImageData(0, 0, S, S);
    const alphaAt = (x: number, y: number) => data[(y * S + x) * 4 + 3];

    // Границы непрозрачной области (высокий порог отсекает сглаженную кромку)
    let minX = S, minY = S, maxX = -1, maxY = -1;
    for (let y = 0; y < S; y++) {
      for (let x = 0; x < S; x++) {
        if (data[(y * S + x) * 4 + 3] >= 250) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }
    if (maxX < 0) return AVATAR_META_FALLBACK;

    // Круглая иконка: занимает почти весь холст, углы прозрачные,
    // середины сторон непрозрачные
    const wide = (maxX - minX + 1) >= S * 0.84 && (maxY - minY + 1) >= S * 0.84;
    const cornersClear =
      alphaAt(1, 1) < 30 && alphaAt(S - 2, 1) < 30 &&
      alphaAt(1, S - 2) < 30 && alphaAt(S - 2, S - 2) < 30;
    const mid = Math.floor(S / 2);
    const sidesSolid =
      alphaAt(mid, 1) >= 200 && alphaAt(mid, S - 2) >= 200 &&
      alphaAt(1, mid) >= 200 && alphaAt(S - 2, mid) >= 200;
    const round = wide && cornersClear && sidesSolid;

    // «Дырки» внутри иконки: у некоторых логотипов буква не нарисована, а
    // ВЫБИТА из плашки (прозрачная, как «P.» у reg.ru). Если залить подложку
    // цветом самой плашки — буква сольётся с ней и исчезнет. Проверяем
    // центральную область: есть прозрачные пиксели — подложку не красим.
    const cx0 = minX + Math.floor((maxX - minX) * 0.25);
    const cx1 = maxX - Math.floor((maxX - minX) * 0.25);
    const cy0 = minY + Math.floor((maxY - minY) * 0.25);
    const cy1 = maxY - Math.floor((maxY - minY) * 0.25);
    let holes = 0;
    let central = 0;
    for (let y = cy0; y <= cy1; y++) {
      for (let x = cx0; x <= cx1; x++) {
        central += 1;
        if (alphaAt(x, y) < 200) holes += 1;
      }
    }
    const hasHoles = central > 0 && holes / central > 0.02;

    // Доминирующий цвет по гистограмме (квантование до 4 бит/канал):
    // не смешивает разные цвета в «средний», как это делало усреднение
    const pickDominant = (ringOnly: boolean): string | null => {
      const buckets = new Map<number, { r: number; g: number; b: number; n: number }>();
      const inset = 3; // кольцо чуть внутри края — мимо антиалиасинга
      for (let y = minY; y <= maxY; y++) {
        for (let x = minX; x <= maxX; x++) {
          const i = (y * S + x) * 4;
          if (data[i + 3] < 250) continue;
          if (ringOnly) {
            const nearEdge =
              x - minX < inset || maxX - x < inset || y - minY < inset || maxY - y < inset;
            if (!nearEdge) continue;
          }
          const r = data[i], g = data[i + 1], b = data[i + 2];
          const key = ((r >> 4) << 8) | ((g >> 4) << 4) | (b >> 4);
          const bucket = buckets.get(key) || { r: 0, g: 0, b: 0, n: 0 };
          bucket.r += r; bucket.g += g; bucket.b += b; bucket.n += 1;
          buckets.set(key, bucket);
        }
      }
      let best: { r: number; g: number; b: number; n: number } | null = null;
      buckets.forEach((v) => { if (!best || v.n > best.n) best = v; });
      const found = best as { r: number; g: number; b: number; n: number } | null;
      if (!found || found.n < 6) return null;
      return `rgb(${Math.round(found.r / found.n)}, ${Math.round(found.g / found.n)}, ${Math.round(found.b / found.n)})`;
    };

    // Сначала кольцо у края (фон логотипа), иначе — вся иконка.
    // С «выбитым» глифом подложку не заливаем — глиф должен просвечивать
    // тёмным фоном аватара, а не цветом плашки
    return { bg: hasHoles ? null : (pickDominant(true) ?? pickDominant(false)), round };
  } catch {
    // canvas tainted (CORS) или иная ошибка — просто без заливки
    return AVATAR_META_FALLBACK;
  }
};

/* Аватар: если иконка сервиса уже известна — показывается сразу; если домен
   встретился впервые — мгновенно видна буква, иконка плавно проявляется
   один раз после загрузки (серверный кэш /api/avatars/:domain) */
const SenderAvatar: React.FC<{ fromEmail: string }> = ({ fromEmail }) => {
  const { hue, letter } = getSenderVisual(fromEmail);
  const domain = senderDomain(fromEmail);
  const alreadyKnown = !!domain && loadedAvatarDomains.has(domain);
  const [loaded, setLoaded] = useState(alreadyKnown);
  const [failed, setFailed] = useState(() => !domain || failedAvatarDomains.has(domain));
  const [iconMeta, setIconMeta] = useState<AvatarIconMeta>(() =>
    (domain ? avatarMetaByDomain.get(domain) : null) ?? AVATAR_META_FALLBACK
  );

  return (
    <div
      className={`email-avatar ${loaded ? 'has-icon' : ''} ${alreadyKnown ? 'instant' : ''}`}
      style={{
        ['--av-hue' as any]: hue,
        ...(loaded && iconMeta.bg ? { background: iconMeta.bg, borderColor: 'rgba(255, 255, 255, 0.12)' } : {})
      }}
      aria-hidden="true"
    >
      <span className="email-avatar-letter">{letter}</span>
      {domain && !failed && (
        <img
          className={`email-avatar-img ${loaded && iconMeta.round ? 'round' : ''}`}
          src={`${apiBaseUrl}/avatars/${domain}?v=2`}
          alt=""
          crossOrigin="anonymous"
          draggable={false}
          onLoad={(e) => {
            loadedAvatarDomains.add(domain);
            if (!avatarMetaByDomain.has(domain)) {
              avatarMetaByDomain.set(domain, analyzeIcon(e.currentTarget));
            }
            setIconMeta(avatarMetaByDomain.get(domain) ?? AVATAR_META_FALLBACK);
            setLoaded(true);
          }}
          onError={() => {
            failedAvatarDomains.add(domain);
            setFailed(true);
          }}
        />
      )}
    </div>
  );
};

const MailboxView: React.FC<MailboxViewProps> = ({ mailboxId, onBack, isWideLayout = false }) => {
  const [emails, setEmails] = useState<Email[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [emailsPage, setEmailsPage] = useState(1);
  const [hasMoreEmails, setHasMoreEmails] = useState(false);
  const [expandedEmail, setExpandedEmail] = useState<string | null>(null);
  const [closingEmail, setClosingEmail] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [copiedCodeEmailId, setCopiedCodeEmailId] = useState<string | null>(null);
  const [emailBodyHeights, setEmailBodyHeights] = useState<Record<string, number | 'auto'>>({});
  const [bodyLoadingIds, setBodyLoadingIds] = useState<Set<string>>(new Set());
  const [pendingOpenEmailId, setPendingOpenEmailId] = useState<string | null>(null);
  const [openProgressByEmailId, setOpenProgressByEmailId] = useState<Record<string, number>>({});
  const [listScrollTop, setListScrollTop] = useState(0);
  const [listViewportHeight, setListViewportHeight] = useState(0);
  const [openEmailContentId, setOpenEmailContentId] = useState<string | null>(null);
  const [openEmailContent, setOpenEmailContent] = useState<EmailContentEntry | null>(null);
  const [detectedEmailCodes, setDetectedEmailCodes] = useState<Record<string, string>>({});
  const [forceRenderHeavyHtmlIds, setForceRenderHeavyHtmlIds] = useState<Set<string>>(new Set());
  const [newEmailIds, setNewEmailIds] = useState<Set<string>>(new Set());
  const emailsCountRef = useRef(0);
  const emailsRef = useRef<Email[]>([]);
  const emailsListRef = useRef<HTMLDivElement | null>(null);
  const loadMoreInFlightRef = useRef(false);
  const copyCodeResetTimerRef = useRef<number | null>(null);
  const openSettleTimerRef = useRef<number | null>(null);
  const openProgressTimerRef = useRef<number | null>(null);
  const expandedEmailRef = useRef<string | null>(null);
  const scrollRafRef = useRef<number | null>(null);
  const pendingScrollTopRef = useRef(0);
  const currentBlobUrlRef = useRef<string | null>(null);
  const newEmailTimersRef = useRef<number[]>([]);
  const preparedOpenHeightRef = useRef<Record<string, number>>({});
  const EMAIL_BODY_ANIM_MS = 300;
  const MAX_INLINE_RENDER_HTML_CHARS = 250000;
  const HEAVY_HTML_SAFE_MODE_CHARS = 250000;
  const MAX_FULL_RENDER_HTML_CHARS = 1200000;

  const capEmails = useCallback((list: Email[]) => {
    if (list.length <= MAX_EMAILS_IN_MEMORY) return list;
    return list.slice(0, MAX_EMAILS_IN_MEMORY);
  }, []);

  const replaceOpenEmailContent = useCallback((next: EmailContentEntry | null) => {
    setOpenEmailContent(prev => {
      if (prev?.blobUrl && prev.blobUrl !== next?.blobUrl) {
        URL.revokeObjectURL(prev.blobUrl);
      }
      return next;
    });
    currentBlobUrlRef.current = next?.blobUrl || null;
  }, []);

  useEffect(() => {
    // Full state reset on mailbox switch to avoid carrying heavy state across mailboxes.
    // Обязательно сбрасываем скролл: иначе виртуализация считает окно от
    // позиции прошлого ящика и список рендерится пустым.
    if (emailsListRef.current) emailsListRef.current.scrollTop = 0;
    setListScrollTop(0);
    setEmails([]);
    setExpandedEmail(null);
    setClosingEmail(null);
    setCopiedCodeEmailId(null);
    setEmailBodyHeights({});
    setBodyLoadingIds(new Set());
    setPendingOpenEmailId(null);
    setOpenProgressByEmailId({});
    setOpenEmailContentId(null);
    replaceOpenEmailContent(null);
    setDetectedEmailCodes({});
    setForceRenderHeavyHtmlIds(new Set());
    setNewEmailIds(new Set());
    preparedOpenHeightRef.current = {};
    newEmailTimersRef.current.forEach(timer => window.clearTimeout(timer));
    newEmailTimersRef.current = [];
    if (openProgressTimerRef.current != null) {
      window.clearInterval(openProgressTimerRef.current);
      openProgressTimerRef.current = null;
    }
  }, [mailboxId, replaceOpenEmailContent]);

  useEffect(() => {
    emailsCountRef.current = emails.length;
    emailsRef.current = emails;
  }, [emails]);

  useEffect(() => {
    const el = emailsListRef.current;
    if (!el) return;

    const updateViewport = () => setListViewportHeight(el.clientHeight || 0);
    updateViewport();
    window.addEventListener('resize', updateViewport);
    return () => window.removeEventListener('resize', updateViewport);
  }, [emails.length]);

  useEffect(() => {
    expandedEmailRef.current = expandedEmail;
  }, [expandedEmail]);

  useEffect(() => {
    return () => {
      if (copyCodeResetTimerRef.current) {
        window.clearTimeout(copyCodeResetTimerRef.current);
      }
      if (openSettleTimerRef.current) {
        window.clearTimeout(openSettleTimerRef.current);
      }
      newEmailTimersRef.current.forEach(timer => window.clearTimeout(timer));
      newEmailTimersRef.current = [];
      if (openProgressTimerRef.current != null) {
        window.clearInterval(openProgressTimerRef.current);
        openProgressTimerRef.current = null;
      }
      if (scrollRafRef.current != null) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
      const url = currentBlobUrlRef.current;
      if (url) {
        URL.revokeObjectURL(url);
        currentBlobUrlRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const debugEnabled = process.env.NODE_ENV !== 'production' || localStorage.getItem('debug_heap') === '1';
    if (!debugEnabled) return;
    const timer = window.setInterval(() => {
      const perfMemory = (performance as any)?.memory;
      const used = Number(perfMemory?.usedJSHeapSize || 0);
      if (!used) return;
      // Lightweight runtime indicator for real heap trends.
      // Enable in production manually with localStorage.debug_heap=1
      console.debug('[heap] usedJSHeapSize MB:', (used / 1024 / 1024).toFixed(1));
    }, 5000);
    return () => window.clearInterval(timer);
  }, []);

  const emailCodeById = useMemo(() => {
    const map = new Map<string, string>();
    const recentEmails = emails.slice(0, CODE_SCAN_EMAILS_LIMIT);
    for (const email of recentEmails) {
      const detectedCode = email.verificationCode || detectedEmailCodes[String(email.id)];
      if (detectedCode) {
        map.set(String(email.id), detectedCode);
        continue;
      }
      const bodySample = (openEmailContentId === String(email.id) ? (openEmailContent?.content || '') : '').slice(0, 3000);
      const code = extractVerificationCode({ subject: email.subject, body: bodySample });
      if (code) {
        map.set(String(email.id), code);
      }
    }
    return map;
  }, [emails, openEmailContentId, openEmailContent, detectedEmailCodes]);

  const prepareHtmlForIframe = useCallback((html: string, allowRemoteImages: boolean) => {
    if (!html) return '';
    return sanitizeEmailHtml(html, {
      allowRemoteImages,
      maxInlineDataUriLength: MAX_INLINE_DATA_URI_LEN
    });
  }, []);

  const htmlToPreviewText = useCallback((html: string) => {
    if (!html) return '';
    return String(html)
      .replace(/<script[\s\S]*?<\/script>/gi, ' ')
      .replace(/<style[\s\S]*?<\/style>/gi, ' ')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<\/div>/gi, '\n')
      .replace(/<[^>]*>/g, ' ')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/[ \t]+\n/g, '\n')
      .replace(/\n{3,}/g, '\n\n')
      .replace(/[ \t]{2,}/g, ' ')
      .trim();
  }, []);

  const readEmailContentHeight = useCallback((emailId: string) => {
    const node = document.getElementById(`email-content-${emailId}`);
    if (!node) return 0;
    const nextHeight = Math.ceil(node.scrollHeight);
    if (!Number.isFinite(nextHeight) || nextHeight <= 0) return 0;
    return nextHeight;
  }, []);

  const loadEmails = useCallback(async (opts?: {
    silent?: boolean;
    append?: boolean;
    targetPage?: number;
  }) => {
    const silent = Boolean(opts?.silent);
    const append = Boolean(opts?.append);
    const targetPage = opts?.targetPage || 1;
    try {
      if (!silent) {
        if (append) setIsLoadingMore(true);
        else setIsLoading(true);
      }

      // Загружаем РЕАЛЬНЫЕ письма из API
      const accessToken = getMailboxAccessToken(mailboxId) || undefined;
      const response = await api.getEmails(mailboxId, targetPage, 50, accessToken, false);

      if (response.success && response.data) {
        const newEmails = response.data.emails || [];
        const pagination = (response.data as any)?.pagination;

        // Проверяем, есть ли новые письма
        // Silent sync intentionally avoids extra logging to keep UI thread lighter.

        if (append) {
          setEmails(prev => {
            const seen = new Set(prev.map(e => String(e.id)));
            const extra = newEmails.filter((e: Email) => !seen.has(String(e.id)));
            return capEmails([...prev, ...extra]);
          });
        } else if (silent) {
          // Тихая фоновая синхронизация первой страницы: обновляем "верх",
          // но сохраняем ранее подгруженные старые письма.
          setEmails(prev => {
            const freshIds = new Set(newEmails.map((e: Email) => String(e.id)));
            const tail = prev.filter(e => !freshIds.has(String(e.id)));
            return capEmails([...newEmails, ...tail]);
          });
        } else {
          setEmails(capEmails(newEmails));
        }

        const totalPages = Number(pagination?.totalPages || 1);
        setEmailsPage(targetPage);
        setHasMoreEmails(targetPage < totalPages);
      } else {
        if (!silent) {
          setEmails([]);
          setEmailsPage(1);
          setHasMoreEmails(false);
        }
      }
    } catch (error) {
      console.error('Error loading emails:', error);
      if (!silent && !append) {
        setEmails([]);
      }
    } finally {
      if (!silent) {
        setIsLoading(false);
        setIsLoadingMore(false);
      }
    }
  }, [capEmails, mailboxId]);

  const buildEmailBlobUrl = useCallback((rawHtml: string, allowRemoteImages: boolean, charLimit = MAX_INLINE_RENDER_HTML_CHARS) => {
    const safeHtml = rawHtml.length > charLimit
      ? rawHtml.slice(0, charLimit)
      : rawHtml;
    const htmlDoc = `<!DOCTYPE html><html><head><meta charset="utf-8"><meta name="viewport" content="width=device-width, initial-scale=1"><base target="_blank"><style>body{margin:0;padding:0;background:white;}</style></head><body>${prepareHtmlForIframe(safeHtml, allowRemoteImages)}</body></html>`;
    const blob = new Blob([htmlDoc], { type: 'text/html;charset=utf-8' });
    return URL.createObjectURL(blob);
  }, [prepareHtmlForIframe]);

  const preloadHtmlBlobHeight = useCallback((blobUrl: string) => {
    return new Promise<number>((resolve) => {
      if (!blobUrl) {
        resolve(0);
        return;
      }

      const iframe = document.createElement('iframe');
      let settled = false;
      const viewportWidth = Math.max(320, (emailsListRef.current?.clientWidth || 900) - 72);

      const cleanup = (height: number) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        iframe.remove();
        resolve(height);
      };

      const measure = () => {
        try {
          const doc = iframe.contentDocument || iframe.contentWindow?.document;
          if (!doc) {
            cleanup(0);
            return;
          }
          const height = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
          cleanup(Number.isFinite(height) && height > 0 ? height + 40 : 0);
        } catch {
          cleanup(0);
        }
      };

      const timeoutId = window.setTimeout(() => cleanup(0), 800);
      iframe.sandbox.add('allow-same-origin', 'allow-popups', 'allow-popups-to-escape-sandbox');
      iframe.referrerPolicy = 'no-referrer';
      iframe.style.position = 'fixed';
      iframe.style.left = '-10000px';
      iframe.style.top = '0';
      iframe.style.width = `${viewportWidth}px`;
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.style.opacity = '0';
      iframe.style.pointerEvents = 'none';
      iframe.onload = () => {
        requestAnimationFrame(() => {
          window.setTimeout(measure, 120);
        });
      };
      iframe.src = blobUrl;
      document.body.appendChild(iframe);
    });
  }, []);

  const estimateHtmlOpenHeight = useCallback((html: string) => {
    const textLength = htmlToPreviewText(html).length;
    return Math.min(1400, Math.max(720, Math.ceil(textLength / 2.4)));
  }, [htmlToPreviewText]);

  const markEmailReadLocally = useCallback((emailId: string) => {
    const targetId = String(emailId);
    setEmails(prev => prev.map(email => (
      String(email.id) === targetId && !email.isRead
        ? { ...email, isRead: true }
        : email
    )));
  }, []);

  const loadHtmlContent = useCallback(async (emailId: string, allowRemoteImages: boolean, forceFull = false) => {
    const targetId = String(emailId);
    const accessToken = getMailboxAccessToken(mailboxId) || undefined;
    const response = await api.getEmail(targetId, accessToken, 'html');
    if (!response.success) return null;
    markEmailReadLocally(targetId);
    const bodyHtml = (response.data as any)?.email?.bodyHtml || '';
    if (!bodyHtml) return null;
    const emailMeta = emailsRef.current.find(e => String(e.id) === targetId);
    const detectedCode = extractVerificationCode({
      subject: emailMeta?.subject || '',
      body: bodyHtml
    });
    const isHeavy = bodyHtml.length > HEAVY_HTML_SAFE_MODE_CHARS;
    if (isHeavy && !forceFull) {
      return {
        mode: 'safe' as const,
        content: htmlToPreviewText(bodyHtml).slice(0, 6000),
        detectedCode
      };
    }
    const blobUrl = buildEmailBlobUrl(
      bodyHtml,
      allowRemoteImages,
      forceFull ? MAX_FULL_RENDER_HTML_CHARS : MAX_INLINE_RENDER_HTML_CHARS
    );
    const measuredHeight = await preloadHtmlBlobHeight(blobUrl);
    const initialHeight = measuredHeight || estimateHtmlOpenHeight(bodyHtml);
    return { mode: 'html' as const, blobUrl, detectedCode, initialHeight };
  }, [buildEmailBlobUrl, htmlToPreviewText, mailboxId, preloadHtmlBlobHeight, estimateHtmlOpenHeight, markEmailReadLocally]);

  const ensureEmailBodyLoaded = useCallback(async (emailId: string): Promise<void> => {
    const targetId = String(emailId);
    const existing = emailsRef.current.find(e => String(e.id) === targetId);
    if (!existing) return;
    if (openEmailContentId === targetId && openEmailContent) return;

    setBodyLoadingIds(prev => {
      if (prev.has(targetId)) return prev;
      const next = new Set(prev);
      next.add(targetId);
      return next;
    });

    try {
      const htmlPayload = await loadHtmlContent(
        targetId,
        true,
        forceRenderHeavyHtmlIds.has(targetId)
      );
      if (htmlPayload) {
        const detectedCode = htmlPayload.detectedCode;
        if (detectedCode) {
          setDetectedEmailCodes(prev => ({ ...prev, [targetId]: detectedCode }));
        }
        const nextEntry: EmailContentEntry = htmlPayload.mode === 'html'
          ? { mode: 'html', content: '', blobUrl: htmlPayload.blobUrl, initialHeight: htmlPayload.initialHeight || 0 }
          : { mode: 'safe', content: htmlPayload.content || '' };
        if (nextEntry.initialHeight) {
          preparedOpenHeightRef.current[targetId] = nextEntry.initialHeight + 40;
        }
        setOpenEmailContentId(targetId);
        replaceOpenEmailContent(nextEntry);
        return;
      }

      // Fallback to text-only when HTML is missing.
      const accessToken = getMailboxAccessToken(mailboxId) || undefined;
      const textResponse = await api.getEmail(targetId, accessToken, 'text');
      if (!textResponse.success) return;
      markEmailReadLocally(targetId);
      const bodyText = (textResponse.data as any)?.email?.bodyText || '';
      const detectedCode = extractVerificationCode({ subject: existing.subject, body: bodyText });
      if (detectedCode) {
        setDetectedEmailCodes(prev => ({ ...prev, [targetId]: detectedCode }));
      }
      const nextEntry: EmailContentEntry = { mode: 'text', content: bodyText || '' };
      preparedOpenHeightRef.current[targetId] = Math.min(900, Math.max(180, Math.ceil((bodyText || '').length / 2.8)));

      setOpenEmailContentId(targetId);
      replaceOpenEmailContent(nextEntry);
    } catch (error) {
      console.error('Error loading full email body:', error);
    } finally {
      setBodyLoadingIds(prev => {
        if (!prev.has(targetId)) return prev;
        const next = new Set(prev);
        next.delete(targetId);
        return next;
      });
    }
  }, [mailboxId, openEmailContentId, openEmailContent, loadHtmlContent, forceRenderHeavyHtmlIds, replaceOpenEmailContent, markEmailReadLocally]);

  const showHeavyHtmlFully = useCallback(async (emailId: string) => {
    const targetId = String(emailId);
    setForceRenderHeavyHtmlIds(prev => {
      if (prev.has(targetId)) return prev;
      const next = new Set(prev);
      next.add(targetId);
      return next;
    });
    try {
      const htmlPayload = await loadHtmlContent(targetId, true, true);
      if (!htmlPayload || htmlPayload.mode !== 'html') return;
      if (openEmailContentId !== targetId) return;
      replaceOpenEmailContent({ mode: 'html', content: '', blobUrl: htmlPayload.blobUrl, initialHeight: htmlPayload.initialHeight || 0 });
    } catch (error) {
      console.error('Error showing heavy HTML fully:', error);
    }
  }, [loadHtmlContent, openEmailContentId, replaceOpenEmailContent]);

  useEffect(() => {
    setEmailsPage(1);
    setHasMoreEmails(false);
    setIsLoadingMore(false);
    loadEmails({ targetPage: 1 });
  }, [loadEmails]);

  // WebSocket only for currently opened mailbox.
  // Keep connection alive while mailbox view is open; service auto-reconnects and re-subscribes.
  useEffect(() => {
    const accessToken = getMailboxAccessToken(mailboxId) || undefined;
    websocketService.subscribeToMailbox(mailboxId, accessToken, (newEmail: Email) => {
      setEmails(prevEmails => {
        // Проверяем, нет ли уже такого письма (по времени и отправителю)
        const exists = prevEmails.some(email => 
          email.fromEmail === newEmail.fromEmail && 
          Math.abs(new Date(email.receivedAt).getTime() - new Date(newEmail.receivedAt).getTime()) < 5000
        );
        
        if (!exists) {
          const newEmailId = String(newEmail.id);
          const wsEmailLight: Email = {
            ...newEmail,
            body: ''
          };
          setNewEmailIds(prev => {
            const next = new Set(prev);
            next.add(newEmailId);
            return next;
          });
          const timer = window.setTimeout(() => {
            setNewEmailIds(prev => {
              if (!prev.has(newEmailId)) return prev;
              const next = new Set(prev);
              next.delete(newEmailId);
              return next;
            });
          }, 2200);
          newEmailTimersRef.current.push(timer);
          return capEmails([wsEmailLight, ...prevEmails]);
        }
        
        return prevEmails;
      });
    });

    setIsConnected(true);

    return () => {
      websocketService.unsubscribeFromMailbox();
      setIsConnected(false);
    };
  }, [capEmails, mailboxId]);

  // Fallback: обычное обновление каждые 5 секунд (если WebSocket не работает)
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      if (!isConnected) {
        loadEmails({ silent: true, targetPage: 1 }); // тихое обновление без показа загрузки
      }
    }, 5000); // каждые 5 секунд как fallback

    return () => clearInterval(interval);
  }, [autoRefresh, isConnected, loadEmails]);

  const clearOpenProgressTimer = useCallback(() => {
    if (openProgressTimerRef.current != null) {
      window.clearInterval(openProgressTimerRef.current);
      openProgressTimerRef.current = null;
    }
  }, []);

  const removeOpenProgress = useCallback((emailId: string) => {
    setOpenProgressByEmailId(prev => {
      if (!(emailId in prev)) return prev;
      const next = { ...prev };
      delete next[emailId];
      return next;
    });
  }, []);

  const sleep = (ms: number) => new Promise(resolve => window.setTimeout(resolve, ms));

  /*
   * Раскрытие/закрытие письма.
   * Анимацию высоты полностью ведёт CSS (grid-template-rows: 0fr -> 1fr),
   * контейнер сам следует реальной высоте контента — без ручных замеров
   * посреди анимации. Здесь только: подготовка контента, классы состояний
   * и один замер после раскрытия для виртуализации списка.
   */
  const toggleEmailExpanded = async (emailId: string) => {
    if (pendingOpenEmailId) return;

    if (expandedEmail === emailId) {
      // Закрытие: снимаем expanded (1fr -> 0fr), контент держим смонтированным
      // до конца анимации, затем выгружаем.
      if (openSettleTimerRef.current) {
        window.clearTimeout(openSettleTimerRef.current);
        openSettleTimerRef.current = null;
      }

      setExpandedEmail(null);
      setClosingEmail(emailId);
      setEmailBodyHeights(prev => ({ ...prev, [emailId]: 0 }));

      window.setTimeout(() => {
        setClosingEmail(prev => (prev === emailId ? null : prev));
        if (expandedEmailRef.current !== emailId && openEmailContentId === emailId) {
          setOpenEmailContentId(null);
          replaceOpenEmailContent(null);
        }
      }, EMAIL_BODY_ANIM_MS);
      return;
    }

    // Открытие: сначала полностью готовим контент (с прогрессом),
    // затем один-единственный проход анимации.
    if (openSettleTimerRef.current) {
      window.clearTimeout(openSettleTimerRef.current);
      openSettleTimerRef.current = null;
    }

    clearOpenProgressTimer();
    setOpenProgressByEmailId({ [emailId]: 0 });
    setPendingOpenEmailId(emailId);
    openProgressTimerRef.current = window.setInterval(() => {
      setOpenProgressByEmailId(prev => {
        const current = prev[emailId] ?? 0;
        if (current >= 92) return prev;
        const step = current < 45 ? 7 : current < 78 ? 4 : 1.8;
        return { ...prev, [emailId]: Math.min(92, current + step) };
      });
    }, 90);

    try {
      await ensureEmailBodyLoaded(emailId);
      clearOpenProgressTimer();
      setOpenProgressByEmailId(prev => ({ ...prev, [emailId]: 100 }));
      await sleep(150);
    } catch (error) {
      console.error('Ошибка подготовки письма:', error);
      clearOpenProgressTimer();
      removeOpenProgress(emailId);
      return;
    } finally {
      setPendingOpenEmailId(null);
    }

    setClosingEmail(null);
    setExpandedEmail(emailId);
    // Ориентировочная высота — только для виртуализации, на стили не влияет.
    setEmailBodyHeights(prev => ({
      ...prev,
      [emailId]: preparedOpenHeightRef.current[emailId] || 0
    }));

    openSettleTimerRef.current = window.setTimeout(() => {
      if (expandedEmailRef.current !== emailId) return;
      const measured = readEmailContentHeight(emailId);
      if (measured > 0) {
        setEmailBodyHeights(prev => (
          prev[emailId] === measured ? prev : { ...prev, [emailId]: measured }
        ));
      }
      removeOpenProgress(emailId);
    }, EMAIL_BODY_ANIM_MS + 60);
  };

  const handleCopyVerificationCode = useCallback(async (
    event: React.MouseEvent<HTMLButtonElement>,
    emailId: string,
    code: string
  ) => {
    event.stopPropagation();
    event.preventDefault();

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(code);
      } else {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-9999px';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }

      setCopiedCodeEmailId(emailId);
      if (copyCodeResetTimerRef.current) {
        window.clearTimeout(copyCodeResetTimerRef.current);
      }
      copyCodeResetTimerRef.current = window.setTimeout(() => {
        setCopiedCodeEmailId(null);
      }, 1400);
    } catch (error) {
      console.error('Ошибка копирования кода:', error);
    }
  }, []);

  const handleLoadMoreEmails = useCallback(() => {
    if (isLoadingMore || !hasMoreEmails || loadMoreInFlightRef.current) return;
    loadMoreInFlightRef.current = true;
    void loadEmails({ append: true, targetPage: emailsPage + 1 }).finally(() => {
      loadMoreInFlightRef.current = false;
    });
  }, [isLoadingMore, hasMoreEmails, emailsPage, loadEmails]);

  const isVirtualized = emails.length > VIRTUALIZATION_MIN_COUNT;

  const handleEmailsListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    if (isVirtualized) {
      pendingScrollTopRef.current = e.currentTarget.scrollTop;
      if (scrollRafRef.current == null) {
        scrollRafRef.current = window.requestAnimationFrame(() => {
          scrollRafRef.current = null;
          setListScrollTop(pendingScrollTopRef.current);
        });
      }
    }
  }, [isVirtualized]);

  const virtualization = useMemo(() => {
    const count = emails.length;
    const useVirtualization = count > VIRTUALIZATION_MIN_COUNT;
    if (count === 0) {
      return {
        start: 0,
        end: 0,
        topSpacer: 0,
        bottomSpacer: 0
      };
    }

    if (!useVirtualization) {
      return {
        start: 0,
        end: count,
        topSpacer: 0,
        bottomSpacer: 0
      };
    }

    const heights = new Array<number>(count);
    const offsets = new Array<number>(count);
    let total = 0;

    for (let i = 0; i < count; i += 1) {
      const id = String(emails[i].id);
      const measured = emailBodyHeights[id];
      const bodyHeight = typeof measured === 'number'
        ? measured
        : (measured === 'auto' ? 820 : 0);
      const expanded = expandedEmail === id || closingEmail === id;
      const h = expanded
        ? Math.max(COLLAPSED_EMAIL_ESTIMATED_HEIGHT, COLLAPSED_EMAIL_ESTIMATED_HEIGHT + bodyHeight + 56)
        : COLLAPSED_EMAIL_ESTIMATED_HEIGHT;

      heights[i] = h;
      offsets[i] = total;
      total += h;
    }

    const viewport = listViewportHeight > 0 ? listViewportHeight : 900;
    // Страховка от устаревшего scrollTop (смена ящика, резкое сокращение
    // списка): окно не может начинаться ниже реального конца контента
    const effectiveScrollTop = Math.min(listScrollTop, Math.max(0, total - viewport));
    const from = Math.max(0, effectiveScrollTop - VIRTUAL_OVERSCAN_PX);
    const to = effectiveScrollTop + viewport + VIRTUAL_OVERSCAN_PX;

    let start = 0;
    while (start < count && (offsets[start] + heights[start]) < from) start += 1;

    let end = start;
    while (end < count && offsets[end] < to) end += 1;

    const topSpacer = start > 0 ? offsets[start] : 0;
    const renderedBottom = end > 0 ? (offsets[end - 1] + heights[end - 1]) : 0;
    const bottomSpacer = Math.max(0, total - renderedBottom);

    return {
      start,
      end,
      topSpacer,
      bottomSpacer
    };
  }, [
    emails,
    emailBodyHeights,
    expandedEmail,
    closingEmail,
    listScrollTop,
    listViewportHeight
  ]);

  if (isLoading) {
    return (
      <div className="mailbox-loading">
        <div className="loading-spinner"></div>
        <p>Загрузка писем...</p>
      </div>
    );
  }

  return (
    <div className={`mailbox-view ${isWideLayout ? 'wide-layout' : ''}`}>
      <div className="emails-list" ref={emailsListRef} onScroll={handleEmailsListScroll}>
        {emails.length === 0 ? (
          <div className="empty-emails">
            <div className="empty-emails-icon" aria-hidden="true">
              <i className="fas fa-inbox"></i>
            </div>
            <p className="empty-emails-title">Писем пока нет</p>
            <p className="empty-emails-hint">Новые письма появятся здесь автоматически</p>
            <div className="empty-actions">
              <button
                onClick={() => loadEmails({ silent: true, targetPage: 1 })}
                onPointerDown={createRipple}
              >
                <i className="fas fa-sync-alt"></i>
                Обновить
              </button>
              <button 
                className={`auto-refresh-toggle ${autoRefresh ? 'active' : ''}`}
                onClick={() => setAutoRefresh(!autoRefresh)}
                onPointerDown={createRipple}
              >
                {/* Вращающаяся иконка = «активно», отдельная строка статуса не нужна
                    (она появлялась/исчезала и сдвигала блок) */}
                <i className={`fas ${autoRefresh ? 'fa-sync-alt fa-spin' : 'fa-play'}`}></i>
                {autoRefresh ? 'Автообновление: ВКЛ' : 'Автообновление: ВЫКЛ'}
              </button>
            </div>
          </div>
        ) : (
          <>
          {virtualization.topSpacer > 0 && (
            <div className="emails-virtual-spacer" style={{ height: `${virtualization.topSpacer}px` }} />
          )}
          {emails.slice(virtualization.start, virtualization.end).map(email => {
            const emailId = String(email.id);
            const verificationCode = emailCodeById.get(emailId);
            const isPreparingOpen = pendingOpenEmailId === emailId;
            const contentEntry = openEmailContentId === emailId ? openEmailContent : null;
            const bodyValue = contentEntry?.content || '';
            return (
            <div 
              key={emailId} 
              className={`email-item ${expandedEmail === emailId ? 'expanded' : ''} ${closingEmail === emailId ? 'closing' : ''} ${isPreparingOpen ? 'preparing-open' : ''} ${verificationCode ? 'has-code' : ''} ${newEmailIds.has(emailId) ? 'new-email' : ''}`}
            >
              <div 
                className="email-header"
                onClick={() => void toggleEmailExpanded(emailId)}
              >
                <SenderAvatar fromEmail={email.fromEmail} />
                <div className="email-info">
                  <div className="email-meta-row">
                    <div className="email-from">{email.fromEmail}</div>
                    <div className="email-date">
                      {isPreparingOpen ? (
                        <span className="email-open-progress" aria-hidden="true">
                          <span style={{ transform: `scaleX(${Math.max(0, Math.min(100, openProgressByEmailId[emailId] ?? 0)) / 100})` }}></span>
                        </span>
                      ) : (
                        formatDateTimeMsk(email.createdAt)
                      )}
                    </div>
                  </div>
                  <div className="email-subject-row">
                    {verificationCode ? (
                      <button
                        type="button"
                        className={`email-subject-copy ${copiedCodeEmailId === emailId ? 'copied' : ''}`}
                        onClick={(event) => handleCopyVerificationCode(event, emailId, verificationCode)}
                        title={`Скопировать код: ${verificationCode}`}
                        aria-label={`Скопировать код ${verificationCode}`}
                      >
                        <i className={`fas ${copiedCodeEmailId === emailId ? 'fa-check' : 'fa-copy'}`} aria-hidden="true"></i>
                        <span className="email-code-value">{verificationCode}</span>
                        <span className="email-code-hint">
                          {copiedCodeEmailId === emailId ? 'скопировано' : 'нажмите, чтобы скопировать'}
                        </span>
                      </button>
                    ) : (
                      <div className="email-subject">{email.subject}</div>
                    )}
                  </div>
                </div>
                <div className="email-toggle">
                  <span
                    className={`email-unread-dot ${email.isRead ? 'read' : ''}`}
                    aria-label={email.isRead ? undefined : 'Непрочитанное письмо'}
                  ></span>
                  <span className="email-toggle-chevron" aria-hidden="true"></span>
                </div>
              </div>
              
              <div className={`email-body ${expandedEmail === emailId ? 'expanded' : ''}`}>
                <div className="email-body-size-proxy">
                {(expandedEmail === emailId || closingEmail === emailId) && (
                  <div
                    id={`email-content-${emailId}`}
                    className="email-content"
                  >
                    {(bodyLoadingIds.has(emailId) || pendingOpenEmailId === emailId) && !bodyValue ? (
                      <div className="email-body-loading">Загружаем содержимое письма...</div>
                    ) : contentEntry?.mode === 'safe' ? (
                      <div>
                        <div className="email-body-loading" style={{ marginBottom: 10 }}>
                          Тяжелое HTML-письмо открыто в безопасном режиме.
                        </div>
                        <pre className="email-text-content">{bodyValue || 'Превью недоступно'}</pre>
                        <div style={{ marginTop: 10 }}>
                          <button
                            type="button"
                            className="refresh-btn"
                            onClick={(event) => {
                              event.stopPropagation();
                              void showHeavyHtmlFully(emailId);
                            }}
                          >
                            Показать полностью
                          </button>
                        </div>
                      </div>
                    ) : contentEntry?.mode === 'html' ? (
                      <>
                        <iframe
                          className="email-html-iframe"
                          src={contentEntry.blobUrl || ''}
                          title="Email content"
                          sandbox="allow-same-origin allow-popups"
                          referrerPolicy="no-referrer"
                          scrolling="no"
                          style={{
                            width: '100%',
                            minHeight: contentEntry.initialHeight ? `${contentEntry.initialHeight}px` : '0px',
                            height: contentEntry.initialHeight ? `${contentEntry.initialHeight}px` : '0px',
                            border: 'none',
                            backgroundColor: 'white',
                            borderRadius: '8px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)'
                          }}
                          onLoad={(e) => {
                            const iframe = e.target as HTMLIFrameElement;
                            const update = () => {
                              try {
                                const doc = iframe.contentDocument || iframe.contentWindow?.document;
                                if (!doc) return;
                                const height = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
                                // Высота iframe анимируется через CSS, grid-контейнер
                                // следует за ней сам — ручной пересчёт не нужен.
                                iframe.style.height = `${height + 40}px`;
                              } catch {
                                // ignore cross-origin/sandbox read issues
                              }
                            };
                            requestAnimationFrame(update);
                            window.setTimeout(update, 120);
                          }}
                        />
                      </>
                    ) : closingEmail === emailId ? (
                      null
                    ) : bodyValue ? (
                      <pre className="email-text-content">{bodyValue}</pre>
                    ) : (
                      <div className="email-body-loading">Содержимое письма недоступно</div>
                    )}
                  </div>
                )}
                </div>
              </div>
            </div>
            );
          })}
          {virtualization.bottomSpacer > 0 && (
            <div className="emails-virtual-spacer" style={{ height: `${virtualization.bottomSpacer}px` }} />
          )}
          </>
        )}
        {isLoadingMore && (
          <div className="emails-lazy-loading">Загружаем старые письма...</div>
        )}
        {!isLoadingMore && hasMoreEmails && (
          <div className="emails-load-more-wrap">
            <button
              type="button"
              className="emails-load-more-btn"
              onClick={handleLoadMoreEmails}
              disabled={loadMoreInFlightRef.current}
            >
              Загрузить еще
            </button>
          </div>
        )}
      </div>
      
      {emails.length > 0 && (
        <div className="mailbox-actions">
          {/* Тихое обновление: не размонтируем экран (и не обрываем ripple) */}
          <button
            className="refresh-btn"
            onClick={() => loadEmails({ silent: true, targetPage: 1 })}
            onPointerDown={createRipple}
          >
            <i className="fas fa-sync-alt"></i> Обновить
          </button>
          <button 
            className={`auto-refresh-btn ${autoRefresh ? 'active' : ''}`}
            onPointerDown={createRipple}
            onClick={() => setAutoRefresh(!autoRefresh)}
            title={autoRefresh ? 'Отключить автообновление' : 'Включить автообновление'}
          >
            <i className={`fas ${autoRefresh ? 'fa-pause' : 'fa-play'}`}></i>
            {autoRefresh ? 'Автообновление: ВКЛ' : 'Автообновление: ВЫКЛ'}
          </button>
        </div>
      )}
    </div>
  );
};

export default MailboxView;
